import { Company } from "@/features/modules/company/objects/Company";
import { companyModel } from "@/features/modules/company/models/CompanyModel";
import { DataHelpers } from "@/core/modules/helpers/DataHelpers";
import { Doctor } from "@/features/modules/doctor/objects/Doctor";
import { doctorModel } from "@/features/modules/doctor/models/DoctorModel";
import { Examination } from "@/features/modules/examination/objects/Examination";
import { Paper } from "@/core/modules/paper/objects/Paper";
import { PaperHelpers } from "@/features/modules/paper/helpers/PaperHelpers";
import { store } from "@/core/modules/store/module";
import { storeTypes } from "@/core/modules/store/types";

export class ExaminationScheduleWithSignaturePaper extends Paper {
  private companyId: string;
  private company: Company | undefined;
  private doctorId: string;
  private doctor: Doctor | undefined;
  private endDate: Date;
  private examinations: Examination[];
  private mode: "All" | "ByCompany" | "ByDoctor" | "ByCompanyAndDoctor";
  private startDate: Date;

  constructor(
    companyId: string,
    doctorId: string,
    startDate: Date,
    endDate: Date,
    examinations: Examination[],
    d: (date: Date, format: string) => string,
    n: (value: number, format: string) => string,
    t: (entry: string, params?: Record<string, unknown>) => string
  ) {
    super(d, n, t);
    this.companyId = companyId;
    this.doctorId = doctorId;
    this.startDate = startDate;
    this.endDate = endDate;
    this.examinations = examinations;
    this.mode = "All";

    this.docDefinition.pageOrientation = "landscape";
  }

  public setStyles(): Record<string, unknown> {
    this.docDefinition.styles = {
      ...super.setStyles(),
      documentTitle: { alignment: "center", bold: true, color: this.highlightColor, fontSize: 14, margin: [0, 0, 0, 0] },
      footerText: { color: this.footerColor, fontSize: 8, italics: true },
      fullName: { bold: true },
      headerText: { color: this.textColor, fontSize: 10 },
      headerField: { fontSize: 6 },
      headerTable: { alignment: "center" },
      headerValue: { border: false, color: this.highlightColor, fontSize: 10 },
      listTable: { fontSize: 8, margin: [0, 5, 0, 15] },
      listTableHeader: { color: this.highlightColor, bold: true, fontSize: 10 },
    };

    return this.docDefinition.styles as Record<string, unknown>;
  }

  public async write(isContinuos = false): Promise<Record<string, unknown>[]> {
    if (this.companyId !== undefined && this.companyId !== "all") {
      this.company = await companyModel.getDocument(this.companyId);
      this.mode = "ByCompany";
    }
    if (this.doctorId !== undefined && this.doctorId !== "all") {
      this.doctor = await doctorModel.getDocument(this.doctorId);
      if (this.mode === "ByCompany") {
        this.mode = "ByCompanyAndDoctor";
      } else {
        this.mode = "ByDoctor";
      }
    }

    this.setTitle(this.t(`statExaminationSchedule.paperTitle${this.mode}`).toUpperCase(), isContinuos);
    this.setAuthor(store.getters[storeTypes.getters.getUser]);
    this.setHighlightColor("#000000");

    const customHeader = await PaperHelpers.getCustomHeader(this.d, this.n, this.t);
    this.setHeader(customHeader);

    const customFooter = PaperHelpers.getCustomFooter(
      this.d,
      this.n,
      this.t,
      "La copia elettronica è conforme all'originale depositato presso lo studio medico"
    );
    this.setFooter(customFooter);

    if (this.mode === "All") {
      this.writeAllHeaderFields();
    } else if (this.mode === "ByCompany") {
      this.writeByCompanyHeaderFields();
    } else if (this.mode === "ByDoctor") {
      this.writeByDoctorHeaderFields();
    } else if (this.mode === "ByCompanyAndDoctor") {
      this.writeByCompanyAndDoctorHeaderFields();
    }

    this.writeBodyTable();

    return this.docDefinition.content as Record<string, unknown>[];
  }

  private writeAllHeaderFields(): void {
    let period: string = this.d(this.startDate, "shortDate");
    if (this.startDate.getTime() !== this.endDate.getTime()) {
      period += ` - ${this.d(this.endDate, "shortDate")}`;
    }
    const headerTableBody: unknown[] = [
      [{ text: period, style: "headerValue", border: [false, false, false, false] }],
      [{ text: this.t("statExaminationSchedule.period"), style: "headerField", border: [false, true, false, false] }],
    ];

    (this.docDefinition.content as Record<string, unknown>[]).push({
      margin: [0, DataHelpers.mmToPoints(5), 0, DataHelpers.mmToPoints(5)],
      style: "headerTable",
      table: {
        body: headerTableBody,
        headerRows: 0,
        widths: ["*"],
      },
      layout: {
        hLineWidth: () => 0.5,
        hLineColor: () => "black",
      },
    });
  }

  private writeByCompanyHeaderFields(): void {
    let period: string = this.d(this.startDate, "shortDate");
    if (this.startDate.getTime() !== this.endDate.getTime()) {
      period += ` - ${this.d(this.endDate, "shortDate")}`;
    }

    const headerTableBody: unknown[] = [
      [
        { text: period ?? "-", style: "headerValue", border: [false, false, false, false] },
        { text: "", border: [false, false, false, false] },
        { text: this.company?.name?.toLocaleUpperCase() ?? "-", style: "headerValue", border: [false, false, false, false] },
      ],
      [
        { text: this.t("statExaminationSchedule.period"), style: "headerField", border: [false, true, false, false] },
        { text: "", border: [false, false, false, false] },
        { text: this.t("statExaminationSchedule.company"), style: "headerField", border: [false, true, false, false] },
      ],
    ];

    (this.docDefinition.content as Record<string, unknown>[]).push({
      margin: [0, DataHelpers.mmToPoints(5), 0, DataHelpers.mmToPoints(5)],
      style: "headerTable",
      table: {
        body: headerTableBody,
        headerRows: 0,
        widths: ["*", DataHelpers.mmToPoints(6), "*"],
      },
      layout: {
        hLineWidth: () => 0.5,
        hLineColor: () => "black",
      },
    });
  }

  private writeByDoctorHeaderFields(): void {
    let period: string = this.d(this.startDate, "shortDate");
    if (this.startDate.getTime() !== this.endDate.getTime()) {
      period += ` - ${this.d(this.endDate, "shortDate")}`;
    }

    const headerTableBody: unknown[] = [
      [
        { text: period ?? "-", style: "headerValue", border: [false, false, false, false] },
        { text: "", border: [false, false, false, false] },
        { text: this.doctor?.fullName?.toLocaleUpperCase() ?? "-", style: "headerValue", border: [false, false, false, false] },
      ],
      [
        { text: this.t("statExaminationSchedule.period"), style: "headerField", border: [false, true, false, false] },
        { text: "", border: [false, false, false, false] },
        { text: this.t("statExaminationSchedule.doctor"), style: "headerField", border: [false, true, false, false] },
      ],
    ];

    (this.docDefinition.content as Record<string, unknown>[]).push({
      margin: [0, DataHelpers.mmToPoints(5), 0, DataHelpers.mmToPoints(5)],
      style: "headerTable",
      table: {
        body: headerTableBody,
        headerRows: 0,
        widths: ["*", DataHelpers.mmToPoints(6), "*"],
      },
      layout: {
        hLineWidth: () => 0.5,
        hLineColor: () => "black",
      },
    });
  }

  private writeByCompanyAndDoctorHeaderFields(): void {
    let period: string = this.d(this.startDate, "shortDate");
    if (this.startDate.getTime() !== this.endDate.getTime()) {
      period += ` - ${this.d(this.endDate, "shortDate")}`;
    }

    const headerTableBody: unknown[] = [
      [
        { text: period ?? "-", style: "headerValue", border: [false, false, false, false] },
        { text: "", border: [false, false, false, false] },
        { text: this.company?.name?.toLocaleUpperCase() ?? "-", style: "headerValue", border: [false, false, false, false] },
        { text: "", border: [false, false, false, false] },
        { text: this.doctor?.fullName?.toLocaleUpperCase() ?? "-", style: "headerValue", border: [false, false, false, false] },
      ],
      [
        { text: this.t("statExaminationSchedule.period"), style: "headerField", border: [false, true, false, false] },
        { text: "", border: [false, false, false, false] },
        { text: this.t("statExaminationSchedule.company"), style: "headerField", border: [false, true, false, false] },
        { text: "", border: [false, false, false, false] },
        { text: this.t("statExaminationSchedule.doctor"), style: "headerField", border: [false, true, false, false] },
      ],
    ];

    (this.docDefinition.content as Record<string, unknown>[]).push({
      margin: [0, DataHelpers.mmToPoints(5), 0, DataHelpers.mmToPoints(5)],
      style: "headerTable",
      table: {
        body: headerTableBody,
        headerRows: 0,
        widths: [DataHelpers.mmToPoints(50), DataHelpers.mmToPoints(6), "*", DataHelpers.mmToPoints(6), DataHelpers.mmToPoints(50)],
      },
      layout: {
        hLineWidth: () => 0.5,
        hLineColor: () => "black",
      },
    });
  }

  private writeBodyTable(): void {
    let examinationIndex = 0;
    const listTableRows: unknown[] = this.examinations.map((examination: Examination) => {
      examinationIndex++;
      return [
        { text: examinationIndex },
        { text: examination.time !== undefined ? this.d(examination.time, "shortDateTime") : "-" },
        [
          { text: examination.employee?.fullName ?? "-" },
          {
            text:
              examination.employee?.birthDate !== undefined
                ? `${this.t("statExaminationSchedule.birthDate")} ${this.d(examination.employee.birthDate, "shortDate")}`
                : "",
          },
        ],
        { text: examination.dutyName ?? "-" },
        [
          { text: examination.type?.name ?? "-" },
          { text: `${this.t("statExaminationSchedule.frequency")}: ${this.t(`frequencies.${examination.frequency}`)}` },
        ],
        { text: "" },
      ];
    });

    const listTableBody: unknown[] = [
      [
        { text: "#", style: "listTableHeader" },
        { text: this.t("statExaminationSchedule.time"), style: "listTableHeader" },
        { text: this.t("statExaminationSchedule.employee"), style: "listTableHeader" },
        { text: this.t("statExaminationSchedule.duty"), style: "listTableHeader" },
        { text: this.t("statExaminationSchedule.examination"), style: "listTableHeader" },
        { text: this.t("statExaminationSchedule.signature"), style: "listTableHeader" },
      ],
      ...listTableRows,
    ];

    (this.docDefinition.content as Record<string, unknown>[]).push({
      style: "listTable",
      table: {
        body: listTableBody,
        headerRows: 1,
        widths: [
          DataHelpers.mmToPoints(8),
          DataHelpers.mmToPoints(20),
          DataHelpers.mmToPoints(50),
          DataHelpers.mmToPoints(50),
          DataHelpers.mmToPoints(60),
          "*",
        ],
      },
      layout: {
        hLineColor: () => "#555555",
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        hLineWidth: (i: number, node: any) => {
          return i === node.table.headerRows ? 2 : 0.5;
        },
        vLineColor: () => "#555555",
        vLineWidth: () => 0.5,
        paddingTop: () => 5,
        paddingBottom: () => 5,
        paddingLeft: () => 8,
        paddingRight: () => 0,
        fillColor: (i: number) => (i % 2 === 0 ? null : this.highlightColor),
        fillOpacity: (i: number) => (i % 2 === 0 ? 0 : 0.05),
      },
    });
  }
}
